import { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, X, ChevronDown } from 'lucide-react';
import CallForm from './landing/CallForm';
import { OPEN_CALL_EVENT } from './landing/motion';
import { trackEvent, Events } from '../lib/analytics';

/**
 * FloatingTryLive: the always-reachable "get a live call" entry point.
 *
 *  - Collapsed: a full-width thumb bar on narrow portrait phones, a compact pill
 *    bottom-right everywhere else (CSS decides by available space).
 *  - Expanded: an anchored panel when the viewport has room (>= 1024 x 600),
 *    otherwise a bottom-sheet dialog (phones, tablets, landscape).
 *  - Both step aside while the hero, the inline #live-demo form, the finale,
 *    or the footer is on screen: each has its own CTA, so nothing is duplicated
 *    or covered.
 *  - Any CTA can open it via openCallForm(source).
 */
// The anchored panel needs real estate; anything smaller (phones, tablets, landscape) gets the sheet.
const PANEL_QUERY = '(min-width: 1024px) and (min-height: 600px)';
const roomForPanel = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia(PANEL_QUERY).matches;

export default function FloatingTryLive({ heroId = 'top', hideOver = ['live-demo'] }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(true);
  const [coveredBy, setCoveredBy] = useState(() => new Set());
  const nameRef = useRef(null);
  const openerRef = useRef(null);
  const pillRef = useRef(null);
  const restoreFocus = useRef(false);

  // Watch the hero and the sections that carry their own CTA (inline form, finale, footer).
  const hideKey = hideOver.join(',');
  useEffect(() => {
    if (!('IntersectionObserver' in window)) { setHeroVisible(false); return undefined; }
    const hero = document.getElementById(heroId);
    const others = hideKey.split(',').map((id) => document.getElementById(id)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === hero) { setHeroVisible(e.isIntersecting); return; }
        setCoveredBy((prev) => {
          const next = new Set(prev);
          if (e.isIntersecting) next.add(e.target.id); else next.delete(e.target.id);
          return next;
        });
      });
    }, { rootMargin: '-80px 0px -35% 0px' }); // top inset = fixed nav height
    if (hero) io.observe(hero); else setHeroVisible(false);
    others.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [heroId, hideKey]);

  const open = useCallback((source = 'floating') => {
    openerRef.current = document.activeElement;
    trackEvent(Events.DEMO_MODAL_OPENED, { source });
    if (roomForPanel()) setPanelOpen(true); else setSheetOpen(true);
    setTimeout(() => nameRef.current?.focus(), 60);
  }, []);

  const close = useCallback(() => {
    restoreFocus.current = true;
    setPanelOpen(false);
    setSheetOpen(false);
  }, []);

  // After closing, return focus to whatever opened the form. If that was the pill,
  // it was unmounted while the panel was open, so focus the freshly rendered pill.
  useEffect(() => {
    if (panelOpen || sheetOpen || !restoreFocus.current) return;
    restoreFocus.current = false;
    const el = openerRef.current;
    const target = el && el.isConnected ? el : pillRef.current;
    if (target && typeof target.focus === 'function') target.focus();
  }, [panelOpen, sheetOpen]);

  useEffect(() => {
    const onOpen = (e) => open(e.detail?.source || 'cta');
    window.addEventListener(OPEN_CALL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CALL_EVENT, onOpen);
  }, [open]);

  useEffect(() => {
    if (!panelOpen && !sheetOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen, sheetOpen, close]);

  // Lock page scroll behind the mobile sheet.
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);

  // Keep Tab inside the mobile dialog while it is open.
  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const nodes = e.currentTarget.querySelectorAll('button:not([disabled]), input, [href], [tabindex]:not([tabindex="-1"])');
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const restHidden = (heroVisible || coveredBy.size > 0) && !panelOpen;

  return (
    <>
      {/* Compact pill (CSS: tablets, landscape, desktop) and the anchored panel */}
      <aside
        data-testid="floating-try-live-desktop"
        aria-label="Try Agent9 live"
        className={`a9l-float ${restHidden || sheetOpen ? 'is-hidden' : ''}`}
      >
        {panelOpen ? (
          <div className="a9l-float__panel" role="dialog" aria-modal="false" aria-labelledby="a9l-float-title">
            <div className="flex items-center justify-between mb-3">
              <span className="a9l-call__status" style={{ color: 'var(--signal)' }}>
                <span className="a9l-dot" aria-hidden="true" /> Live demo
              </span>
              <button type="button" data-testid="try-live-desktop-toggle" onClick={close} className="a9l-menu-btn" aria-label="Close live demo">
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
            <h2 id="a9l-float-title" className="text-lg font-semibold leading-tight">Experience Your Own AI Receptionist</h2>
            <p className="a9l-note mt-1.5 mb-4" style={{ fontSize: '0.82rem' }}>Enter your business website and hear how an AI assistant could answer calls for your company.</p>
            <CallForm ref={nameRef} source="floating-desktop" />
          </div>
        ) : (
          <button ref={pillRef} type="button" data-testid="try-live-desktop-open" className="a9l-btn a9l-btn--signal a9l-float__pill" onClick={() => open('floating-desktop-pill')}>
            <Phone size={16} aria-hidden="true" /> Hear Agent9 live
          </button>
        )}
      </aside>

      {/* Thumb bar (CSS: narrow portrait phones) */}
      <div data-testid="floating-try-live-mobile-bar" className={`a9l-mbar ${restHidden || sheetOpen ? 'is-hidden' : ''}`}>
        <button type="button" data-testid="try-live-mobile-open" onClick={() => open('floating-mobile-bar')} className="a9l-btn a9l-btn--signal w-full">
          <Phone size={16} aria-hidden="true" /> Get a live call from Agent9
        </button>
      </div>

      {sheetOpen && (
        <div className="a9l-sheet" data-testid="floating-try-live-mobile-sheet">
          <div className="a9l-sheet__scrim" onClick={close} aria-hidden="true" />
          <div className="a9l-sheet__body" role="dialog" aria-modal="true" aria-labelledby="a9l-sheet-title" onKeyDown={trapTab}>
            <div className="a9l-sheet__handle mx-auto mb-4 h-1 w-12 rounded-full" style={{ background: 'var(--line-2)' }} aria-hidden="true" />
            <div className="a9l-sheet__grid">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="a9l-call__status" style={{ color: 'var(--signal)' }}>
                    <span className="a9l-dot" aria-hidden="true" /> Live demo
                  </span>
                  <button type="button" data-testid="try-live-mobile-close" onClick={close} className="a9l-menu-btn" aria-label="Close live demo">
                    <X size={18} aria-hidden="true" />
                  </button>
                </div>
                <h2 id="a9l-sheet-title" className="text-xl font-semibold leading-tight">Experience Your Own AI Receptionist</h2>
                <p className="a9l-body mt-2" style={{ fontSize: '0.9rem' }}>Enter your business website and hear how an AI assistant could answer calls for your company.</p>
              </div>
              <CallForm ref={nameRef} source="floating-mobile" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
